/**
 * Pure, deterministic reducer for LR Direct voice-builder corrections.
 *
 * The AI (voice-post "builder_correct" mode) returns ONLY ops from a strict
 * allowlist; this reducer is the single place they are applied. Guarantees:
 *  - unknown ops are REJECTED, never applied
 *  - update/remove target STABLE STOP IDS, never array positions
 *  - fields/stops the ops don't mention stay untouched
 *  - the input load is never mutated
 *  - after applying, the load is re-validated (incl. the delivery-after-pickup
 *    guard) so a correction that creates an invalid state is caught
 */

import {
  normalizePosition,
  normalizeStopType,
  newVoiceStopId,
  VOICE_MAX_DROPS,
  VOICE_MAX_PICKUPS,
  VOICE_OP_TYPES,
  type VoiceBuilderLoad,
  type VoiceBuilderStop,
  type VoiceOp,
} from "./voiceBuilderTypes";
import { isSpokenDatePhrase, resolveDatePhrase } from "./resolveDatePhrase";

export interface RejectedOp {
  op: unknown;
  reason: string;
}

export interface VoiceLoadConfirmation {
  stopId?: string;
  field: string;
  note: string;
}

export interface VoiceLoadValidation {
  /** Hard blockers (missing pickup/drop, missing equipment, date order, ...). */
  issues: string[];
  /** Soft blockers the user must confirm (ambiguities, unresolvable dates). */
  confirmations: VoiceLoadConfirmation[];
  /** No hard blockers. */
  valid: boolean;
  /** No hard blockers AND nothing left to confirm — safe to post. */
  readyToPost: boolean;
}

export interface ApplyVoiceOpsResult {
  load: VoiceBuilderLoad;
  applied: VoiceOp[];
  rejected: RejectedOp[];
  warnings: string[];
  validation: VoiceLoadValidation;
}

export interface ApplyVoiceOpsOptions {
  /** Client's local today — used to re-run date validation. Tests pass a fixed date. */
  today?: Date;
  /** Injectable stop-id generator for deterministic tests. */
  idGen?: () => string;
}

// ---------------------------------------------------------------------------
// Scalar parsers (AI sends strings; code owns the numbers)
// ---------------------------------------------------------------------------

/** "41.5k" | "41,500" | 41500 | "41500 lbs" → 41500 */
export function parseWeightLbs(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  let s = v.trim().toLowerCase().replace(/[$,]/g, "").replace(/\s*(lbs?|pounds?)\s*$/, "");
  const kMatch = /^([\d.]+)\s*k$/.exec(s);
  if (kMatch) s = String(parseFloat(kMatch[1]) * 1000);
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/** "53" | "53 ft" | "53-foot" | 53 → 53 (sanity range 28-75 ft). */
export function parseLengthFt(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  let s = v.trim().toLowerCase().replace(/[,]/g, "").replace(/\s*(ft|feet|foot|')\s*$/, "");
  const n = Number(s);
  if (!Number.isFinite(n) || n < 28 || n > 75) return null;
  return Math.round(n);
}

/** "-10" | "34" | "34F" | 34 → 34 (sanity range for freight temps). */
export function parseTemperatureF(v: unknown): number | null {

  if (typeof v === "string" && !v.trim()) return null;
  const s = String(v).trim().toLowerCase()
    .replace(/[°]/g, "")
    .replace(/\s*(degrees?|deg)?\s*(f|fahrenheit|c|celsius)?\s*$/, "");
  const n = Number(s);
  if (!Number.isFinite(n) || n < -50 || n > 150) return null;
  return Math.round(n);
}

// ---------------------------------------------------------------------------
// Validation (re-runs after every correction; also used standalone)
// ---------------------------------------------------------------------------

const isReefer = (equipment: string | null): boolean =>
  !!equipment && /reefer|refrigerated/i.test(equipment);

export function validateVoiceLoad(load: VoiceBuilderLoad, today: Date): VoiceLoadValidation {
  const issues: string[] = [];
  const confirmations: VoiceLoadConfirmation[] = [];

  const pickups = load.stops.filter((s) => s.stop_type === "pickup");
  const drops = load.stops.filter((s) => s.stop_type === "drop");

  if (pickups.length === 0) issues.push("The load needs at least one pickup stop.");
  if (drops.length === 0) issues.push("The load needs at least one delivery stop.");

  load.stops.forEach((s, i) => {
    const label = `${s.stop_type === "pickup" ? "Pickup" : "Delivery"} ${i + 1}`;
    if (!s.city.trim()) issues.push(`${label} is missing a city.`);
    if (!s.state.trim()) {
      confirmations.push({
        stopId: s.id,
        field: `stop.${s.id}.state`,
        note: `${label} (${s.city || "unknown"}) has no state — confirm it.`,
      });
    }
  });

  // Dates: resolve every raw phrase deterministically; flag anything unresolvable.
  const pickupDates: string[] = [];
  const dropDates: string[] = [];
  for (const s of load.stops) {
    if (!s.date_text) continue;
    const r = resolveDatePhrase(s.date_text, today);
    if (!r.date || r.needsConfirmation) {
      confirmations.push({
        stopId: s.id,
        field: `stop.${s.id}.date`,
        note: r.note ?? `Heard "${s.date_text}" — please confirm the date.`,
      });
    }
    if (r.date) (s.stop_type === "pickup" ? pickupDates : dropDates).push(r.date);
  }

  // Delivery-after-pickup guard (overall first pickup vs overall last delivery).
  if (pickupDates.length && dropDates.length) {
    const firstPickup = pickupDates.slice().sort()[0];
    const lastDelivery = dropDates.slice().sort().slice(-1)[0];
    if (lastDelivery < firstPickup) {
      issues.push("Delivery date can't be before the pickup date.");
    }
  }

  if (!load.equipment) issues.push("Equipment type is missing.");
  // Weight and length are optional — they default to 41,000 lbs / 53 ft in
  // the mapper (voiceLoadToWizard.ts) and never block posting.
  if (isReefer(load.equipment) && load.temperature_f === null) {
    confirmations.push({
      field: "temperature_f",
      note: "Reefer load with no temperature — confirm the set point.",
    });
  }


  // Anything the parser flagged can never silently pass.
  for (const a of load.ambiguities) {
    confirmations.push({
      field: a.field,
      note: a.candidates.length
        ? `Heard "${a.heard}" — confirm: ${a.candidates.join(" / ")}.`
        : `Heard "${a.heard}" — please confirm.`,
    });
  }

  const valid = issues.length === 0;
  return { issues, confirmations, valid, readyToPost: valid && confirmations.length === 0 };
}

// ---------------------------------------------------------------------------
// The reducer
// ---------------------------------------------------------------------------

export function applyVoiceOps(
  currentLoad: VoiceBuilderLoad,
  ops: unknown[],
  opts: ApplyVoiceOpsOptions = {},
): ApplyVoiceOpsResult {
  const today = opts.today ?? new Date();
  const idGen = opts.idGen ?? newVoiceStopId;

  const next: VoiceBuilderLoad = structuredClone(currentLoad);
  const applied: VoiceOp[] = [];
  const rejected: RejectedOp[] = [];
  const warnings: string[] = [];

  const findStop = (id: string | null | undefined): { stop: VoiceBuilderStop; index: number } | null => {
    if (!id) return null;
    const index = next.stops.findIndex((s) => s.id === id);
    return index >= 0 ? { stop: next.stops[index], index } : null;
  };

  for (const raw of ops) {
    const op = (raw ?? {}) as VoiceOp;

    // STRICT allowlist — anything outside is rejected, never applied.
    if (typeof op.op !== "string" || !(VOICE_OP_TYPES as readonly string[]).includes(op.op)) {
      rejected.push({ op: raw, reason: `Unknown op "${String(op?.op ?? raw)}" — outside the allowlist.` });
      continue;
    }

    switch (op.op) {
      case "set_equipment": {
        const v = String(op.value ?? "").trim();
        if (!v) { rejected.push({ op: raw, reason: "set_equipment requires a value." }); break; }
        next.equipment = v;
        applied.push(op);
        break;
      }

      case "set_weight": {
        const n = parseWeightLbs(op.value);
        if (n === null) { rejected.push({ op: raw, reason: `set_weight: couldn't parse "${String(op.value)}" as pounds.` }); break; }
        next.weight_lbs = n;
        applied.push(op);
        break;
      }

      case "set_length": {
        const n = parseLengthFt(op.value);
        if (n === null) { rejected.push({ op: raw, reason: `set_length: couldn't parse "${String(op.value)}" as feet.` }); break; }
        next.length_ft = n;
        applied.push(op);
        break;
      }


      case "set_commodity": {
        const v = String(op.value ?? "").trim();
        if (!v) { rejected.push({ op: raw, reason: "set_commodity requires a value." }); break; }
        next.commodity = v;
        applied.push(op);
        break;
      }

      case "set_temperature": {
        const n = parseTemperatureF(op.value);
        if (n === null) { rejected.push({ op: raw, reason: `set_temperature: couldn't parse "${String(op.value)}" as °F.` }); break; }
        next.temperature_f = n;
        applied.push(op);
        break;
      }

      case "set_notes": {
        const v = String(op.value ?? "").trim();
        if (!v) { rejected.push({ op: raw, reason: "set_notes requires a value." }); break; }
        next.notes = v;
        applied.push(op);
        break;
      }

      case "set_pickup_date":
      case "set_delivery_date": {
        const wantType = op.op === "set_pickup_date" ? "pickup" : "drop";
        const dateText = String(op.date_text ?? op.value ?? "").trim();
        // Junk tokens ("null") mean no date was actually spoken — not an op.
        if (!isSpokenDatePhrase(dateText)) { rejected.push({ op: raw, reason: `${op.op} requires a spoken date phrase.` }); break; }
        if (op.stop_id) {
          const found = findStop(op.stop_id);
          if (!found) { rejected.push({ op: raw, reason: `${op.op}: stop id "${op.stop_id}" not found.` }); break; }
          if (found.stop.stop_type !== wantType) {
            rejected.push({ op: raw, reason: `${op.op}: stop "${op.stop_id}" is a ${found.stop.stop_type}, not a ${wantType}.` });
            break;
          }
          found.stop.date_text = dateText;
        } else {
          const targets = next.stops.filter((s) => s.stop_type === wantType);
          if (targets.length === 0) {
            rejected.push({ op: raw, reason: `${op.op}: the load has no ${wantType} stops.` });
            break;
          }
          for (const s of targets) s.date_text = dateText;
          if (targets.length > 1) {
            warnings.push(`${op.op}: no specific stop named — applied to all ${targets.length} ${wantType} stops.`);
          }
        }
        applied.push(op);
        break;
      }

      case "add_stop": {
        const type = normalizeStopType(op.stop_type);
        if (!type) { rejected.push({ op: raw, reason: "add_stop requires stop_type pickup|drop." }); break; }
        const city = String(op.city ?? "").trim();
        if (!city) { rejected.push({ op: raw, reason: "add_stop requires a city." }); break; }
        const count = next.stops.filter((s) => s.stop_type === type).length;
        const cap = type === "pickup" ? VOICE_MAX_PICKUPS : VOICE_MAX_DROPS;
        if (count >= cap) {
          rejected.push({ op: raw, reason: `add_stop: already at the ${cap}-${type} limit.` });
          break;
        }
        const state = String(op.state ?? "").trim().toUpperCase().slice(0, 2);
        const stop: VoiceBuilderStop = {
          id: idGen(),
          stop_type: type,
          city,
          state,
          date_text: op.date_text != null && isSpokenDatePhrase(String(op.date_text)) ? String(op.date_text).slice(0, 80) : null,
        };
        const pickupCount = next.stops.filter((s) => s.stop_type === "pickup").length;
        const pos = normalizePosition(op.position) ?? (type === "pickup" ? "last_pickup" : "last_drop");
        let insertAt: number;
        switch (pos) {
          case "first_pickup": insertAt = 0; break;
          case "last_pickup": insertAt = pickupCount; break;
          case "first_drop": insertAt = pickupCount; break;
          case "last_drop": insertAt = next.stops.length; break;
        }
        next.stops.splice(insertAt, 0, stop);
        if (!state) {
          warnings.push(`add_stop: "${city}" has no state — flagged for confirmation.`);
          next.ambiguities.push({ field: `stop.${stop.id}.state`, heard: city, candidates: [] });
        }
        applied.push(op);
        break;
      }

      case "update_stop": {
        const found = findStop(op.stop_id);
        if (!found) { rejected.push({ op: raw, reason: `update_stop: stop id "${String(op.stop_id)}" not found.` }); break; }
        const { stop } = found;
        let changed = false;

        if (op.city != null && String(op.city).trim()) {
          stop.city = String(op.city).trim();
          changed = true;
        }
        if (op.state != null && String(op.state).trim()) {
          stop.state = String(op.state).trim().toUpperCase().slice(0, 2);
          changed = true;
        }
        if (op.date_text != null && isSpokenDatePhrase(String(op.date_text))) {
          stop.date_text = String(op.date_text).trim().slice(0, 80);
          changed = true;
        }
        if (op.stop_type != null) {
          const newType = normalizeStopType(op.stop_type);
          if (!newType) { rejected.push({ op: raw, reason: `update_stop: unknown stop_type "${op.stop_type}".` }); break; }
          if (newType !== stop.stop_type) {
            const count = next.stops.filter((s) => s.stop_type === newType).length;
            const cap = newType === "pickup" ? VOICE_MAX_PICKUPS : VOICE_MAX_DROPS;
            if (count >= cap) {
              rejected.push({ op: raw, reason: `update_stop: already at the ${cap}-${newType} limit.` });
              break;
            }
            // Move to the end of the new type group.
            next.stops.splice(found.index, 1);
            stop.stop_type = newType;
            const insertAt = newType === "pickup"
              ? next.stops.filter((s) => s.stop_type === "pickup").length
              : next.stops.length;
            next.stops.splice(insertAt, 0, stop);
            changed = true;
          }
        }

        if (!changed) { rejected.push({ op: raw, reason: "update_stop had nothing to apply." }); break; }
        applied.push(op);
        break;
      }

      case "remove_stop": {
        const found = findStop(op.stop_id);
        if (!found) { rejected.push({ op: raw, reason: `remove_stop: stop id "${String(op.stop_id)}" not found.` }); break; }
        const remaining = next.stops.filter((s) => s.stop_type === found.stop.stop_type).length - 1;
        if (remaining === 0) {
          rejected.push({
            op: raw,
            reason: `remove_stop: can't remove the last ${found.stop.stop_type} stop.`,
          });
          break;
        }
        next.stops.splice(found.index, 1);
        applied.push(op);
        break;
      }

      case "set_handling": {
        const v = String(op.value ?? "").trim();
        if (!v) { rejected.push({ op: raw, reason: "set_handling requires a value." }); break; }
        const vNorm = v.toLowerCase();
        const action = (op.action ?? "add").trim().toLowerCase();
        if (action === "add") {
          if (next.handling.some((h) => h.toLowerCase() === vNorm)) {
            warnings.push(`set_handling: "${v}" is already listed.`);
          } else {
            next.handling.push(v);
          }
          applied.push(op);
        } else if (action === "remove") {
          const before = next.handling.length;
          next.handling = next.handling.filter((h) => h.toLowerCase() !== vNorm);
          if (next.handling.length === before) {
            warnings.push(`set_handling: "${v}" wasn't on the load.`);
          }
          applied.push(op);
        } else {
          rejected.push({ op: raw, reason: `set_handling: unknown action "${op.action}".` });
        }
        break;
      }
    }
  }

  return {
    load: next,
    applied,
    rejected,
    warnings,
    validation: validateVoiceLoad(next, today),
  };
}
